import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { PageSkeleton } from '@shared/ui/PageSkeleton/PageSkeleton.js';
import { AdminRoute } from './AdminRoute.js';
import { RouteErrorFallback } from './RouteErrorFallback.js';
import { CANONICAL_HOST, ROUTES } from '@shared/config/constants.js';
import { useSiteResolve } from '@entities/domain/model/useDomains.js';

const PublicHomePage = lazy(() => import('@pages/public-home/PublicHomePage.js'));
const TenantHomePage = lazy(() => import('@pages/tenant-home/TenantHomePage.js'));
const LoginPage = lazy(() => import('@pages/login/LoginPage.js'));
const AdminDashboardPage = lazy(() => import('@pages/admin-dashboard/AdminDashboardPage.js'));
const AdminLeaguesListPage = lazy(() => import('@pages/admin-leagues-list/AdminLeaguesListPage.js'));
const AdminLeagueCreatePage = lazy(() => import('@pages/admin-league-create/AdminLeagueCreatePage.js'));
const AdminLeagueEditPage = lazy(() => import('@pages/admin-league-edit/AdminLeagueEditPage.js'));
const AdminFixtureEditorPage = lazy(() => import('@pages/admin-fixture-editor/AdminFixtureEditorPage.js'));
const AdminResultadosPage = lazy(() => import('@pages/admin-resultados/AdminResultadosPage.js'));
const AdminFinalesPage = lazy(() => import('@pages/admin-finales/AdminFinalesPage.js'));
const PublicLeagueViewPage = lazy(() => import('@pages/public-league-view/PublicLeagueViewPage.js'));
const PublicEquipoFixturePage = lazy(() => import('@pages/public-equipo-fixture/PublicEquipoFixturePage.js'));
const AdminDomainsPage = lazy(() => import('@pages/admin-domains/AdminDomainsPage.js'));
const NotFoundPage = lazy(() => import('@pages/not-found/NotFoundPage.js'));

// ── Helpers ────────────────────────────────────────────────────────────────

function Wrap({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary FallbackComponent={RouteErrorFallback}>
      <Suspense fallback={<PageSkeleton />}>{children}</Suspense>
    </ErrorBoundary>
  );
}

/**
 * Whether the current browser hostname is the canonical ligalaffi.com host.
 * Evaluated once at module load — safe because a page can't change its own hostname.
 */
const isCanonicalHost = window.location.hostname === CANONICAL_HOST;

/**
 * Root component for custom-domain hosts.
 * Calls GET /api/site/resolve to find out whether the host is mapped to a tenant or a league,
 * then redirects to the corresponding SPA path. Admin routes are unavailable on custom domains
 * (IAM only accepts the canonical origin) and redirect to the canonical admin panel.
 */
function CustomDomainRoot() {
  const { data, isLoading, isError } = useSiteResolve(true);

  if (isLoading) return <PageSkeleton />;

  if (isError || !data || data.type === 'canonical') {
    return <Navigate to={ROUTES.home} replace />;
  }

  if (data.type === 'tenant' && data.slug) {
    return <Navigate to={`/u/${encodeURIComponent(data.slug)}`} replace />;
  }

  if (data.type === 'league') {
    return <Navigate to={`/ligas/${data.leagueId}`} replace />;
  }

  return <Wrap><NotFoundPage /></Wrap>;
}

// ── Routes ─────────────────────────────────────────────────────────────────

export function AppRoutes() {
  return (
    <Routes>
      {/* Public — available on both canonical and custom domains */}
      <Route
        path="/"
        element={
          <Wrap>
            {isCanonicalHost ? <PublicHomePage /> : <CustomDomainRoot />}
          </Wrap>
        }
      />
      <Route path="/u/:slug" element={<Wrap><TenantHomePage /></Wrap>} />
      <Route path="/ligas/:leagueId" element={<Wrap><PublicLeagueViewPage /></Wrap>} />
      <Route
        path="/ligas/:leagueId/equipos/:teamId/fixture"
        element={<Wrap><PublicEquipoFixturePage /></Wrap>}
      />
      <Route path="/login" element={<Wrap><LoginPage /></Wrap>} />

      {/* Admin — only available on the canonical host.
          On a custom domain, redirect to the canonical admin panel. */}
      <Route
        path="/admin"
        element={
          isCanonicalHost
            ? <Wrap><AdminRoute><AdminDashboardPage /></AdminRoute></Wrap>
            : <Navigate to={`${import.meta.env.VITE_PUBLIC_BASE_URL ?? ''}/admin`} replace />
        }
      />
      <Route
        path="/admin/ligas"
        element={<Wrap><AdminRoute><AdminLeaguesListPage /></AdminRoute></Wrap>}
      />
      <Route
        path="/admin/ligas/nueva"
        element={<Wrap><AdminRoute><AdminLeagueCreatePage /></AdminRoute></Wrap>}
      />
      <Route
        path="/admin/ligas/:leagueId"
        element={<Wrap><AdminRoute><AdminLeagueEditPage /></AdminRoute></Wrap>}
      />
      <Route
        path="/admin/ligas/:leagueId/divisiones/:divisionId/fixture"
        element={<Wrap><AdminRoute><AdminFixtureEditorPage /></AdminRoute></Wrap>}
      />
      <Route
        path="/admin/ligas/:leagueId/divisiones/:divisionId/resultados"
        element={<Wrap><AdminRoute><AdminResultadosPage /></AdminRoute></Wrap>}
      />
      <Route
        path="/admin/ligas/:leagueId/divisiones/:divisionId/finales"
        element={<Wrap><AdminRoute><AdminFinalesPage /></AdminRoute></Wrap>}
      />
      <Route
        path={ROUTES.adminDomains}
        element={<Wrap><AdminRoute><AdminDomainsPage /></AdminRoute></Wrap>}
      />

      <Route path="*" element={<Wrap><NotFoundPage /></Wrap>} />
    </Routes>
  );
}
